import React, { useState, useEffect, useCallback } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import RatingStars from './Components/RatingStars';
import CircularProgress from '@mui/material/CircularProgress';
import useRatingGet from './api/useRatingGet';
import ratingPost from './api/useRatingPost';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './i18n';

const App = (): JSX.Element => {
    const { t } = useTranslation();
    const url = window.location.href;
    const clientRating = Number(window.localStorage.getItem(`${url}`)) || 0;
    const { ratingAvg, ratingSum, ratingCount, isLoading } = useRatingGet(url);
    const [rating, setRating] = useState<number>(0);
    const [rated, setRated] = useState<boolean>(window.localStorage.getItem(`${url}`) ? true : false);
    const [ratingInactive, setRatingInactive] = useState<boolean>(false);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [legend, setLegend] = useState<string>(t('not_rated_yet'));
    const [captchaToken, setCaptchaToken] = useState<string>('');
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return;
        }

        const captchaToken = await executeRecaptcha();
        setCaptchaToken(captchaToken);
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
        setRating(ratingAvg);

        if (ratingAvg > 0) {
            setLegend(`${ratingAvg}/5 ${t('rating_count')} ${ratingCount}`);
        }
    }, [ratingAvg, ratingCount, t, handleReCaptchaVerify]);

    useEffect(() => {
        if (rated) {
            setRatingInactive(true);
        }
    }, [rated]);

    const onRatingChange = (
        event: React.SyntheticEvent<Element, Event>,
        newValue: number | null
    ): void => {
        if (!rated && newValue !== null) {
            setRating(newValue);
            setLegend(`Twoja ocena to: ${newValue}`);
            setButtonDisabled(false);
        }
    };

    const onClick = (): void => {
        ratingPost(url, rating, captchaToken);
        const newAverage: number
        = Math.round(((ratingSum + rating) / (ratingCount + 1)) * 10) / 10;
        setRating(newAverage);
        setLegend(`${newAverage}/5 ${t('rating_count')} ${ratingCount + 1}`);
        setButtonDisabled(true);
        setRated(true);
        window.localStorage.setItem(`${url}`, `${rating}`);
        setRatingInactive(true);
    };

    return (
        <div className="container">
            {isLoading
                ? (
                    <CircularProgress color="error" />
                )
                : (
                    <RatingStars rating={rating} onRatingChange={onRatingChange} onClick={onClick}
                        ratingInactive={ratingInactive} legend={legend} rated={rated}
                        buttonDisabled={buttonDisabled} clientRating={clientRating}
                    />
                )}
        </div>
    );
};

export default App;
